import type {
  DividerCdtLevel,
  DividerHardware,
  DividerPolicy,
  DividersSetByHand,
} from "@CommonTypes/backend/PspRules";
import { DIVIDER_CDT_LEVELS } from "@CommonTypes/backend/PspRules";
import type { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import type { ResponseGetCdtCategoryTree } from "@CommonApi/cdt/categories/_categoryId_/tree";
import type { CdtTreeItem } from "@CommonApi/cdt/trees/_treeId_/items";
import {
  getBaseDividerEvidence,
  keptFixtureDividerShelves,
  loaderShelfKey,
  resolveDividerPolicy,
  type DividerAttributeValue,
  type DividerPolicyProduct,
  type FixtureDividerRow,
  type LoaderLibraryShelf,
} from "@CommonUtils/pog/dividerPolicy";
import { getActiveShelfDividers } from "@CommonUtils/pog/shelfDividers";
import { translate } from "src/i18n/translate";
import { formatLength } from "src/utils/lengthUnit";
import { keptLoaderMode, loaderDepthProblem, loaderRulesState } from "./loaderRulesModel";

/**
 * The section's default: the products that sit on divider shelves in the base
 * planogram. Stored as source "base" until someone edits the ticks; any edit
 * stores the ticked list as source "items".
 */
export const DEFAULT_DIVIDER_POLICY: DividerPolicy = { source: "base" };

/** The length unit the rules modal passes to the Dividers section (see src/utils/lengthUnit). */
export { DEFAULT_LENGTH_UNIT } from "src/utils/lengthUnit";

const POLICY_SOURCES = new Set(["base", "attribute", "cdt", "items", "none"]);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** A stored or imported policy narrowed to its shape; anything else is the default. */
export const readDividerPolicy = (value: unknown): DividerPolicy => {
  if (typeof value !== "object" || value === null) return DEFAULT_DIVIDER_POLICY;
  const raw = value as Record<string, unknown>;
  if (typeof raw.source !== "string" || !POLICY_SOURCES.has(raw.source)) {
    return DEFAULT_DIVIDER_POLICY;
  }
  const strings = (list: unknown) =>
    Array.isArray(list) ? list.filter((item): item is string => typeof item === "string") : [];
  const hardware = raw.hardware as Record<string, unknown> | null | undefined;
  return {
    source: raw.source as DividerPolicy["source"],
    ...(typeof raw.attribute === "string" ? { attribute: raw.attribute } : {}),
    ...(raw.values !== undefined ? { values: strings(raw.values) } : {}),
    ...(DIVIDER_CDT_LEVELS.includes(raw.cdtLevel as DividerCdtLevel)
      ? { cdtLevel: raw.cdtLevel as DividerCdtLevel }
      : {}),
    ...(raw.cdtValues !== undefined ? { cdtValues: strings(raw.cdtValues) } : {}),
    ...(raw.productCodes !== undefined ? { productCodes: strings(raw.productCodes) } : {}),
    hardware:
      hardware && isFiniteNumber(hardware.width)
        ? {
            width: hardware.width,
            height: isFiniteNumber(hardware.height) ? hardware.height : 0,
            depth: isFiniteNumber(hardware.depth) ? hardware.depth : 0,
            atStart: Boolean(hardware.atStart),
            atEnd: Boolean(hardware.atEnd),
          }
        : null,
    ...(typeof raw.keepFixtureDividers === "boolean"
      ? { keepFixtureDividers: raw.keepFixtureDividers }
      : {}),
    ...(raw.loaderProductCodes !== undefined
      ? { loaderProductCodes: strings(raw.loaderProductCodes) }
      : {}),
    ...(isFiniteNumber(raw.loaderDepth) && raw.loaderDepth > 0
      ? { loaderDepth: raw.loaderDepth }
      : {}),
    // Absent (every rule written before the choice) reads "Follow the products".
    ...(raw.loaderMode === "products" || raw.loaderMode === "shelf"
      ? { loaderMode: raw.loaderMode }
      : {}),
  };
};

/** A product's value on each CDT level, as the "Group by" list groups it. */
export type DividerGroupValues = Partial<Record<DividerCdtLevel, string | null | undefined>>;

/** A product as the Dividers section lists it. */
export interface DividerRulesProduct extends DividerPolicyProduct {
  name?: string | null;
  /**
   * What "Group by" reads: the category's CDT tree when it places any product,
   * else the base planogram's CDT fields. Absent: the product's `cdt`.
   */
  groupValues?: DividerGroupValues;
}

/**
 * Each product's value on every CDT level from the category's CDT tree (the
 * tree a Studio release writes): its node's name on the node's level, and its
 * ancestors' names on theirs. Products without a node are left out.
 */
export const cdtValuesFromCdtTree = (
  tree: Pick<ResponseGetCdtCategoryTree, "nodes"> | null | undefined,
  items: readonly Pick<CdtTreeItem, "sku" | "nodeId">[],
): Map<string, DividerGroupValues> => {
  const nodes = new Map((tree?.nodes ?? []).map((node) => [node.id, node]));
  const result = new Map<string, DividerGroupValues>();
  for (const item of items) {
    if (item.nodeId === null || !item.sku || result.has(item.sku)) continue;
    const values: DividerGroupValues = {};
    const seen = new Set<number>();
    let node = nodes.get(item.nodeId);
    while (node && !seen.has(node.id)) {
      seen.add(node.id);
      const level = DIVIDER_CDT_LEVELS[node.level];
      if (level && values[level] === undefined) values[level] = node.name;
      node = node.parentNodeId === null ? undefined : nodes.get(node.parentNodeId);
    }
    if (Object.keys(values).length > 0) result.set(item.sku, values);
  }
  return result;
};

/**
 * The products the rules section reads: every base planogram product (in wall
 * order) with its CDT values, then the category's other products; attributes
 * and names by product code. When the category's CDT tree places any product
 * (`treeValuesByProductCode`), "Group by" reads the tree and a product the
 * tree leaves out goes to "No group": a base imported from a PSA stores blank
 * CDT fields, so the base planogram's own fields are only the fallback.
 */
export const dividerRulesProducts = (
  basePog: PogNonCircular | null,
  attributesByProductCode: ReadonlyMap<string, Record<string, DividerAttributeValue>>,
  namesByProductCode: ReadonlyMap<string, string | null> = new Map(),
  treeValuesByProductCode: ReadonlyMap<string, DividerGroupValues> = new Map(),
): DividerRulesProduct[] => {
  const byTree = treeValuesByProductCode.size > 0;
  const treeValues = (productCode: string) =>
    byTree ? { groupValues: treeValuesByProductCode.get(productCode) ?? {} } : {};
  const products = new Map<string, DividerRulesProduct>();
  for (const bay of basePog?.planogram?.bays ?? []) {
    for (const shelf of bay.shelves ?? []) {
      for (const item of shelf.items ?? []) {
        if (products.has(item.productCode)) continue;
        products.set(item.productCode, {
          productCode: item.productCode,
          name: item.name || namesByProductCode.get(item.productCode) || null,
          cdt: { cdt0: item.variant, cdt1: item.cdt1, cdt2: item.cdt2, cdt3: item.cdt3 },
          attributes: attributesByProductCode.get(item.productCode),
          ...treeValues(item.productCode),
        });
      }
    }
  }
  for (const [productCode, attributes] of attributesByProductCode) {
    if (products.has(productCode)) continue;
    products.set(productCode, {
      productCode,
      name: namesByProductCode.get(productCode) ?? null,
      attributes,
      ...treeValues(productCode),
    });
  }
  return [...products.values()];
};

/** Lengths are stored x100 of the merchant's unit. */
export const toDisplayLength = (hundredths: number) => Number((hundredths / 100).toFixed(2));
export const fromDisplayLength = (value: number) => Math.max(0, Math.round(value * 100));

/** The base planogram's divider size, if it has a usable one. */
export const baseDividerHardware = (basePog: PogNonCircular | null) =>
  getBaseDividerEvidence(basePog).hardware;

/** The divider size in use: the policy's own when it has a width, else the base's. */
export const dividerHardwareInUse = (
  policy: DividerPolicy,
  baseHardware: DividerHardware | null,
): DividerHardware | null =>
  policy.hardware && policy.hardware.width > 0 ? policy.hardware : baseHardware;

/** Product codes that get dividers under the policy (the ticked products). */
export const productsWithDividers = (
  policy: DividerPolicy,
  products: readonly DividerPolicyProduct[],
  basePog: PogNonCircular | null,
): Set<string> => {
  const { needs } = resolveDividerPolicy({ policy, products, basePog });
  return new Set([...needs].filter(([, need]) => need.needed).map(([code]) => code));
};

/** The two tick columns of the product list. */
export type TickColumn = "dividers" | "loaders";

/** What each column has ticked now, whatever the rule's source. */
export interface TickedColumns {
  dividers: ReadonlySet<string>;
  loaders: ReadonlySet<string>;
}

/**
 * The policy with these products ticked or unticked in one column; the result
 * is always a picked list, and it writes both columns, so the untouched one
 * keeps what it showed (the backend reads a missing list as nothing ticked).
 */
export const setProductsTicked = (
  policy: DividerPolicy,
  ticked: TickedColumns,
  products: readonly DividerPolicyProduct[],
  column: TickColumn,
  codes: readonly string[],
  isTicked: boolean,
): DividerPolicy => {
  const next = new Set(ticked[column]);
  for (const code of codes) {
    if (isTicked) next.add(code);
    else next.delete(code);
  }
  const inListOrder = (set: ReadonlySet<string>) =>
    products.map((product) => product.productCode).filter((code) => set.has(code));
  return {
    source: "items",
    productCodes: inListOrder(column === "dividers" ? next : ticked.dividers),
    loaderProductCodes: inListOrder(column === "loaders" ? next : ticked.loaders),
    hardware: policy.hardware ?? null,
    ...(policy.keepFixtureDividers ? { keepFixtureDividers: true } : {}),
    ...(policy.loaderDepth ? { loaderDepth: policy.loaderDepth } : {}),
    ...keptLoaderMode(policy),
  };
};

/** Clear all: nothing ticked in either column; the sizes and the fixture switch stay. */
export const clearAllTicks = (policy: DividerPolicy): DividerPolicy => ({
  source: "items",
  productCodes: [],
  loaderProductCodes: [],
  hardware: policy.hardware ?? null,
  ...(policy.keepFixtureDividers ? { keepFixtureDividers: true } : {}),
  ...(policy.loaderDepth ? { loaderDepth: policy.loaderDepth } : {}),
  ...keptLoaderMode(policy),
});

/**
 * Back to the base planogram: both columns follow the base and both sizes
 * (divider width and ends, loader depth) come from it again (review B.2); the
 * fixture switch and the spring loader choice stay.
 */
export const resetToBase = (policy: DividerPolicy): DividerPolicy => ({
  source: "base",
  hardware: null,
  ...(policy.keepFixtureDividers ? { keepFixtureDividers: true } : {}),
  ...keptLoaderMode(policy),
});

/** The category's own name for each CDT level, where the CDT pages show one. */
export type DividerLevelNames = Partial<Record<DividerCdtLevel, string | null>>;

/**
 * Level names as the CDT tree editor shows them: the subdivision field of the
 * first node on each level.
 */
export const levelNamesFromCdtTree = (tree: ResponseGetCdtCategoryTree): DividerLevelNames => {
  const names: DividerLevelNames = {};
  for (const node of tree.nodes) {
    const level = DIVIDER_CDT_LEVELS[node.level];
    const name = node.subdivisionField?.trim();
    if (level && name && !names[level]) names[level] = name;
  }
  return names;
};

/** A "Group by" option: the category's level name, else "Level 1".."Level 4". */
export const dividerGroupLevelLabel = (level: DividerCdtLevel, names: DividerLevelNames = {}) =>
  names[level]?.trim() ||
  translate("common", "planogramRules.dividers.level", {
    level: DIVIDER_CDT_LEVELS.indexOf(level) + 1,
  });

export interface DividerProductGroup {
  /** The CDT value, or "" for products without one. */
  value: string;
  products: DividerRulesProduct[];
}

/** The value "Group by" reads for a product on one level (the tree first, see `groupValues`). */
export const productGroupValue = (product: DividerRulesProduct, level: DividerCdtLevel) =>
  String((product.groupValues ?? product.cdt)?.[level] ?? "").trim();

/** Products grouped by one CDT level, in first-seen (wall) order; no value last. */
export const groupProductsByCdt = (
  products: readonly DividerRulesProduct[],
  level: DividerCdtLevel,
): DividerProductGroup[] => {
  const groups = new Map<string, DividerProductGroup>();
  for (const product of products) {
    const value = productGroupValue(product, level);
    const group = groups.get(value) ?? { value, products: [] };
    group.products.push(product);
    groups.set(value, group);
  }
  const ungrouped = groups.get("");
  groups.delete("");
  return [...groups.values(), ...(ungrouped ? [ungrouped] : [])];
};

export interface BaseShelfDividerResult {
  bayNo: number;
  shelfNo: number;
  /** Facings of products that get dividers, of all facings on the shelf. */
  needing: number;
  facings: number;
  /** Strictly more than half: the pogger's follow-products rule. */
  byProducts: boolean;
  /** The base store's fixture row has valid dividers and the rule keeps them. */
  keptFromFixture: boolean;
  /** The base store's fixture row's hand override; it decides before the products, as on generation. */
  setByHand: DividersSetByHand | null;
  hasDividers: boolean;
}

/**
 * The base store's fixture rows (GET /merchflow/base-pog/:basePogId
 * `fixtureDividerRows`). Null: O can't see the store's fixtures (no base store
 * resolved, as on a UHD or PSA-imported base; an older backend; base mode), so
 * no shelf is claimed as kept. Absent (a caller with no fixture evidence at
 * all): the base planogram's own shelf dividers stand in.
 */
export type FixtureDividerRows = readonly FixtureDividerRow[] | null | undefined;

/**
 * Without fixture rows: the base shelf's own dividers, when the DEV-3516
 * guard trusts them (the base planogram usually comes from the same import).
 */
const hasValidShelfDividers = (
  shelf: PogNonCircular["planogram"]["bays"][number]["shelves"][number],
) => {
  const active = getActiveShelfDividers(shelf.dividers);
  if (!active) return false;
  const flagsOk = [active.atStart, active.atEnd].every((flag) => Number(flag ?? 0) <= 1);
  return flagsOk && !(Number(shelf.width) > 0 && Number(active.width) >= Number(shelf.width));
};

/**
 * What the rule does to each base shelf that holds products, in generation's
 * order: the base store's row's hand override first ("never": none, "always":
 * dividers), then the products. A shelf the products leave bare is kept from
 * the fixture when the base store's row for it keeps its dividers
 * (`keptFixtureDividerShelves`, the backend's keep rule).
 * Null rows (the store's fixtures can't be seen) keep nothing; absent rows
 * fall back to the base shelf's own valid dividers.
 */
export const baseShelfDividerResults = (
  basePog: PogNonCircular | null,
  withDividers: ReadonlySet<string>,
  keepFixtureDividers = false,
  fixtureRows?: FixtureDividerRows,
): BaseShelfDividerResult[] => {
  const results: BaseShelfDividerResult[] = [];
  const keptRows = fixtureRows
    ? keptFixtureDividerShelves({ policy: { keepFixtureDividers }, rows: fixtureRows })
    : null;
  const handSet = new Map(
    (fixtureRows ?? []).map((row) => [
      loaderShelfKey(row.bayNo, row.shelfNo),
      row.dividersSetByHand,
    ]),
  );
  (basePog?.planogram?.bays ?? []).forEach((bay, bayIndex) => {
    (bay.shelves ?? []).forEach((shelf, shelfIndex) => {
      if (shelf.type && shelf.type !== "REGULAR") return;
      const items = shelf.items ?? [];
      if (items.length === 0) return;
      let needing = 0;
      let facings = 0;
      for (const item of items) {
        const itemFacings = Math.max(1, Number(item.facings) || 0);
        facings += itemFacings;
        if (withDividers.has(item.productCode)) needing += itemFacings;
      }
      const byProducts = needing * 2 > facings;
      const bayNo = bayIndex + 1;
      const shelfNo = shelfIndex + 1;
      const setByHand = handSet.get(loaderShelfKey(bayNo, shelfNo)) ?? null;
      const keptFromFixture =
        !setByHand &&
        keepFixtureDividers &&
        !byProducts &&
        fixtureRows !== null &&
        (keptRows ? keptRows.has(loaderShelfKey(bayNo, shelfNo)) : hasValidShelfDividers(shelf));
      results.push({
        bayNo,
        shelfNo,
        needing,
        facings,
        byProducts,
        keptFromFixture,
        setByHand,
        hasDividers: setByHand ? setByHand === "always" : byProducts || keptFromFixture,
      });
    });
  });
  return results;
};

/**
 * One result row, in parts so the verdict can be styled:
 * "Bay 1 Shelf 1 · dividers · loader 1.5 in · 18 of 21 facings". A bare shelf
 * says why; a kept one says where its dividers come from. `loader` is the
 * spring-loader word ("loader 1.5 in" / "no loader"), when given.
 */
export const describeBaseShelfResult = (shelf: BaseShelfDividerResult, loader?: string) => {
  const name = translate("common", "planogramRules.dividers.result.name", {
    bay: shelf.bayNo,
    shelf: shelf.shelfNo,
  });
  const verdict = translate(
    "common",
    shelf.hasDividers
      ? "planogramRules.dividers.result.dividers"
      : "planogramRules.dividers.result.noDividers",
  );
  const counts = { needing: shelf.needing, facings: shelf.facings };
  const detail = shelf.setByHand
    ? translate(
        "common",
        shelf.setByHand === "always"
          ? "planogramRules.dividers.result.setAlways"
          : "planogramRules.dividers.result.setNever",
      )
    : shelf.keptFromFixture
      ? translate("common", "planogramRules.dividers.result.keptFromFixture")
      : shelf.hasDividers
        ? translate("common", "planogramRules.dividers.result.facings", counts)
        : translate("common", "planogramRules.dividers.result.needsMoreThanHalf", counts);
  return {
    name,
    verdict,
    loader,
    detail,
    text: [name, verdict, loader, detail].filter(Boolean).join(" · "),
  };
};

/**
 * The result panel's first line: "20 of 21 shelves get dividers; 18 get spring
 * loaders." (without the loader count: "20 of 21 shelves get dividers.").
 */
export const describeBaseShelfTotal = (
  shelves: readonly BaseShelfDividerResult[],
  loaderShelves?: number,
) => {
  const counts = {
    count: shelves.filter((shelf) => shelf.hasDividers).length,
    total: shelves.length,
  };
  return loaderShelves === undefined
    ? translate("common", "planogramRules.dividers.result.total", counts)
    : translate("common", "planogramRules.dividers.result.totalWithLoaders", {
        ...counts,
        loaders: loaderShelves,
      });
};

/** Why the policy cannot be generated with as it stands, or null. */
export const dividerPolicyProblem = (
  policy: DividerPolicy,
  { needing, hardware }: { needing: number; hardware: DividerHardware | null },
): string | null =>
  policy.source !== "none" && needing > 0 && !(hardware?.width ?? 0)
    ? translate("common", "planogramRules.dividers.errors.widthMissing")
    : null;

/**
 * The problem for a policy, computed from the base planogram and products:
 * the divider width first, then the spring loader depth.
 */
export const dividerPolicyProblemFor = (
  policy: DividerPolicy,
  basePog: PogNonCircular | null,
  products: readonly DividerPolicyProduct[],
  { libraryShelves = [], unit }: { libraryShelves?: readonly LoaderLibraryShelf[]; unit: string },
) =>
  dividerPolicyProblem(policy, {
    needing: productsWithDividers(policy, products, basePog).size,
    hardware: dividerHardwareInUse(policy, baseDividerHardware(basePog)),
  }) ??
  loaderDepthProblem(policy, loaderRulesState(policy, products, basePog, libraryShelves), unit);

/** The base planogram shows dividers, but none of them can be trusted (DEV-3516 faults). */
export const baseDividerDataLooksWrong = (basePog: PogNonCircular | null) => {
  const evidence = getBaseDividerEvidence(basePog);
  const faulty = evidence.widthNotBelowShelf + evidence.flagsOutOfRange;
  return { isWrong: faulty > 0 && evidence.hardware === null, faultyShelves: faulty };
};

/**
 * A rule that follows the base planogram, on a base whose divider data can't
 * be used and with no width of its own, falls back to no dividers, so
 * generation is not blocked. Applied once when the base planogram loads (and
 * when a config is imported), never on later edits; Reset stays disabled on
 * such a base, so the rule cannot flip back.
 */
export const fallBackFromUnusableBase = (
  policy: DividerPolicy,
  basePog: PogNonCircular | null,
): DividerPolicy =>
  policy.source === "base" &&
  !(policy.hardware?.width ?? 0) &&
  baseDividerDataLooksWrong(basePog).isWrong
    ? { ...policy, source: "none" }
    : policy;

/**
 * O's one sentence about dividers, computed (no model call). The Dividers
 * section shows it by default; spring loaders add their own sentence after it.
 * Every form keeps the same order: products, shelves, width, then the keep
 * clause.
 */
export const describeDividersForO = ({
  policy,
  basePog,
  products,
  unit,
  isLoading = false,
  fixtureRows,
}: {
  policy: DividerPolicy;
  basePog: PogNonCircular | null;
  products: readonly DividerPolicyProduct[];
  unit: string;
  isLoading?: boolean;
  /** The base store's fixture rows; the shelf counts read the keep switch from them. */
  fixtureRows?: FixtureDividerRows;
}): string => {
  if (isLoading) return translate("common", "planogramRules.dividers.o.reading");
  const keepFixture = policy.keepFixtureDividers === true;
  const keep = keepFixture ? translate("common", "planogramRules.dividers.o.keepClause") : "";
  // A picked list does not need the base planogram; the base rule and its fallback do.
  if (!basePog && (policy.source === "base" || policy.source === "none")) {
    return translate("common", "planogramRules.dividers.o.noBase", { keep });
  }

  const evidence = getBaseDividerEvidence(basePog);
  if (policy.source === "none" && baseDividerDataLooksWrong(basePog).isWrong) {
    const shelvesText = (count: number) =>
      translate(
        "common",
        count === 1 ? "planogramRules.dividers.o.oneShelf" : "planogramRules.dividers.o.shelves",
        { count },
      );
    const faults = [
      evidence.widthNotBelowShelf > 0 &&
        translate("common", "planogramRules.dividers.o.faultWidth", {
          shelves: shelvesText(evidence.widthNotBelowShelf),
        }),
      evidence.flagsOutOfRange > 0 &&
        translate("common", "planogramRules.dividers.o.faultFlags", {
          shelves: shelvesText(evidence.flagsOutOfRange),
        }),
    ].filter((fault): fault is string => typeof fault === "string");
    const [first = "", second] = faults;
    return translate(
      "common",
      second
        ? "planogramRules.dividers.o.badBaseFaults"
        : "planogramRules.dividers.o.badBaseOneFault",
      {
        faults: second
          ? translate("common", "planogramRules.dividers.o.faultsJoin", { first, second })
          : first,
        keep,
      },
    );
  }
  if (policy.source === "base" && evidence.dividerShelves === 0) {
    return translate("common", "planogramRules.dividers.o.noneInBase", { keep });
  }

  const ticked = productsWithDividers(policy, products, basePog);
  const shelves = baseShelfDividerResults(basePog, ticked, keepFixture, fixtureRows);
  const withDividers = shelves.filter((shelf) => shelf.hasDividers).length;
  const hardware = dividerHardwareInUse(policy, evidence.hardware);
  const width = hardware?.width
    ? translate("common", "planogramRules.dividers.o.width", {
        width: formatLength(hardware.width, unit),
      })
    : null;

  if (ticked.size === 0) {
    if (!keepFixture) return translate("common", "planogramRules.dividers.o.noneOnAnyShelf");
    // No base store resolved: no shelf is claimed as kept (the result rows keep none either).
    if (fixtureRows === null) {
      return translate("common", "planogramRules.dividers.o.noneNeededNoFixtures");
    }
    const kept = {
      kept: shelves.filter((shelf) => shelf.keptFromFixture).length,
      total: shelves.length,
    };
    return width
      ? translate("common", "planogramRules.dividers.o.noneNeededKeptWithWidth", {
          ...kept,
          width,
        })
      : translate("common", "planogramRules.dividers.o.noneNeededKept", kept);
  }
  const productCount = translate("common", "planogramRules.dividers.o.productCount", {
    count: ticked.size,
    total: products.length,
  });
  if (!width) {
    return translate("common", "planogramRules.dividers.o.noWidthYet", {
      products: productCount,
      keep,
    });
  }
  const shelfCount = translate("common", "planogramRules.dividers.o.shelfCount", {
    count: withDividers,
    total: shelves.length,
  });
  return translate(
    "common",
    policy.source === "base"
      ? "planogramRules.dividers.o.likeBase"
      : "planogramRules.dividers.o.forProducts",
    { products: productCount, shelves: shelfCount, width, keep },
  );
};

export const describeBaseShelfResultWithLoader = (
  shelf: BaseShelfDividerResult,
  loader: string,
) => {
  const { name, verdict } = describeBaseShelfResult(shelf);
  const counts = { needing: shelf.needing, facings: shelf.facings };
  const detail = shelf.setByHand
    ? translate(
        "common",
        shelf.setByHand === "always"
          ? "planogramRules.dividers.result.setAlways"
          : "planogramRules.dividers.result.setNever",
      )
    : shelf.keptFromFixture
      ? translate("common", "planogramRules.dividers.result.keptFromFixture")
      : shelf.hasDividers
        ? translate("common", "planogramRules.dividers.result.count", counts)
        : translate("common", "planogramRules.dividers.result.countNeedsMoreThanHalf", counts);
  return { name, verdict, detail, loader, text: `${name} · ${verdict} ${detail} · ${loader}` };
};
